import { createContext, useContext, type ReactNode } from 'react';

/**
 * WHAT `src/app/` PUTS INSIDE THE EDITOR PANE THAT THE EDITOR FEATURE MAY NOT BUILD ITSELF.
 *
 * ── the problem this solves ───────────────────────────────────────────────────────────────
 *
 * docs/ARCHITECTURE.md §3 describes `EditorPanel.tsx` as "size banner + CodeMirrorHost +
 * ProblemsPanel inside a boundary". But `ProblemsPanel.tsx` belongs to `features/lint/`, and
 * gate 2 denies a deep import into a neighbouring feature: composition happens in
 * `features/registry.ts` and `src/app/`, never between two features (ADR-0003).
 *
 * That rule is right, and the way through it is not a relative `../lint/ProblemsPanel` — which
 * would pass ESLint on a technicality and reintroduce exactly the coupling the gate exists to
 * prevent — but a SLOT. The editor owns the pane's LAYOUT: which region sits above the code,
 * which below, what scrolls, what is `position: relative` so the toast region can be pinned
 * inside it (docs/DESIGN.md §8). It does not own the CONTENT of those regions, and with this
 * context it cannot accidentally start to: a slot is a `ReactNode`, which has no API.
 *
 * ── why a context rather than a prop ──────────────────────────────────────────────────────
 *
 * `ViewDescriptor` hands a panel exactly `{ view, api }` — a pure projection and a command
 * channel — and `ViewHost` spreads that into a module it lazily imported and knows nothing
 * about. There is no prop channel from `src/app/` to a panel, by design (ADR-0020). Context is
 * the channel that survives a lazy boundary, and the same reasoning that put
 * `EditorDocumentContext` in its own CodeMirror-free module applies here: `src/app/` mounts the
 * provider eagerly, so this module must not reach the engine. It imports React and nothing else.
 *
 * Every field is optional and the default is `{}`. An `EditorPanel` mounted with no provider —
 * which is how `EditorPanel.test.tsx` mounts it — renders the editor and omits the regions,
 * rather than crashing or inventing English of its own.
 */
/**
 * ONE APPLICATION SHORTCUT, structurally.
 *
 * `src/app/keymap.ts` derives the keyboard from the same `commands` array the toolbar is built
 * from (ADR-0014, the DRY requirement), and therefore has to hand the editor a list of
 * bindings. It cannot hand it `KeyBinding` from `@codemirror/view`: gate 2 denies
 * `@codemirror/*` everywhere but `src/features/editor/**`, and the composition root is not an
 * exception to that — an `Extension` returned from `src/app/` would put the engine's types,
 * and the temptation to reach for its values, in the one layer that imports everything.
 *
 * So the contract is structural. This is the subset of `KeyBinding` the application uses, and
 * `EditorPanel` passes it straight to `CodeMirrorHost`, where it IS a `KeyBinding`:
 * `key: string` satisfies `key?: string`, and `run(): boolean` satisfies
 * `run?: (view: EditorView) => boolean` because a function that ignores an argument may stand
 * in for one that receives it. Nothing is cast anywhere along that path.
 */
export interface EditorKeyBinding {
  /** CodeMirror keymap syntax — `'Mod-Enter'`, `'Shift-F8'`. Same string the button prints. */
  readonly key: string;
  /** `true` keeps the browser's own binding from also firing. */
  readonly preventDefault?: boolean;
  /** `false` lets CodeMirror's own binding for the same key run instead. */
  run(): boolean;
}

export interface EditorSlots {
  /**
   * Below the code: `features/lint/ProblemsPanel`, or the valid-state colophon of
   * docs/DESIGN.md §6.10 — the two REPLACE each other, so they are deliberately one slot.
   */
  readonly problems?: ReactNode;
  /**
   * Pinned inside the code area. `<Toaster/>` must be mounted here rather than at the document
   * root: the `≤640px` rule in `Toaster.module.css` turns the region `static` so it DISPLACES
   * the code instead of covering it, and a region mounted at the root cannot displace anything.
   */
  readonly notifications?: ReactNode;
  /** The auto-repair `<Banner/>` of docs/DESIGN.md §6.5, between the toolbar and the code. */
  readonly repair?: ReactNode;
  /**
   * `Paste from clipboard` / `Load an example` for the empty state (docs/DESIGN.md §6.8). They
   * are `Command`s, which a feature may not resolve — `CommandContext` lives in `src/app/`.
   */
  readonly emptyActions?: ReactNode;
  /**
   * `Format without the editor` / `Validate only` / `Clear buffer` for the blocked state
   * (docs/DESIGN.md §6.9). "Exits, not a dead end" is the whole point of that screen, and the
   * exits are commands.
   */
  readonly blockedActions?: ReactNode;

  /**
   * The keycap to print on each hint row of the empty state (docs/DESIGN.md §6.8), already
   * rendered for THIS platform: `'⌘⏎'` or `'Ctrl+Enter'`.
   *
   * Data rather than markup, and from here rather than from the feature, for the two reasons
   * every other field in this block exists: the keys belong to `Command`s and only
   * `src/app/` can resolve one, and this feature is compiled without `navigator` (gate 1) so
   * it cannot decide between `⌘` and `Ctrl`. Omitted — which is how `EditorPanel.test.tsx`
   * mounts it — the rows render as sentences, which is what they did everywhere before.
   */
  readonly hintKeys?: {
    readonly format?: string;
    readonly compact?: string;
    readonly nextProblem?: string;
  };

  // ── tuning the composition root owns ───────────────────────────────────────────────────

  /**
   * The product's keyboard shortcuts, derived by `src/app/keymap.ts` from the SAME `commands`
   * array the toolbar is projected from (ADR-0014). They are given `Prec.high` by
   * `buildExtensions`, so a product binding always beats CodeMirror's default for the same key
   * — which is deliberate for `Mod-Enter` (`insertBlankLine`) and `Mod-Backspace`
   * (`deleteGroupBackward`), the two collisions docs/DESIGN.md §7.4 knowingly accepts.
   *
   * Omitted means the editor keeps only its own keymap, which is how `EditorPanel.test.tsx`
   * mounts it.
   */
  readonly appKeymap?: readonly EditorKeyBinding[];

  /**
   * Above this many UTF-8 bytes a pasted document is inserted verbatim instead of being
   * repaired (`pasteHandler.ts`). Repair is synchronous and on the main thread, so this is the
   * question "how much text may we afford to rewrite inside a dispatch" — a different question
   * from the store's hard limit, and one whose answer is a number.
   *
   * It arrives from here rather than being declared in this feature because EVERY tunable
   * number of the application lives in `src/app/config.ts` (`WORKER_THRESHOLD_BYTES` is the
   * intended value), and a feature may not import the composition root. Omitted means no cap.
   */
  readonly pasteRepairMaxBytes?: number;

  /**
   * The size ladder, for the blocked state's meter and band table (docs/DESIGN.md §6.9).
   *
   * DESIGN is explicit that the meter is drawn TO SCALE — a 14.2 MB buffer puts the soft tick
   * at 14.1 % and the hard tick at 70.4 % — so the panel needs the two thresholds as numbers,
   * not as a band. They live in `src/app/config.ts` (`SOFT_LIMIT_BYTES`, `HARD_LIMIT_BYTES`,
   * `debounceMsFor(SOFT_LIMIT_BYTES)`), which a feature may not import.
   *
   * Omitted, the blocked screen still renders its label, headline, explanation and exits — it
   * simply does not draw a chart it cannot put a scale on. "Not to scale is worse than a
   * sentence" is DESIGN's own rule, applied to its absence.
   */
  readonly limits?: {
    readonly softBytes: number;
    readonly hardBytes: number;
    /** Lint debounce in the 2–10 MiB band, for `editor.blocked.bandLargeEffect`. */
    readonly largeDebounceMs: number;
  };
}

const EMPTY: EditorSlots = {};

export const EditorSlotsContext = createContext<EditorSlots>(EMPTY);

/** Never throws: an unprovided pane is a valid pane with empty regions. */
export function useEditorSlots(): EditorSlots {
  return useContext(EditorSlotsContext);
}
